/*
 * Wisdom signup. One script, three places: the footer band, the first-visit
 * popup, and the Balloon Pop gate (the game won't start until you've entered
 * an email). Everything posts to Klaviyo's public client subscribe endpoint,
 * so there is no server of ours in the loop and no API secret in the theme.
 *
 * Config comes from window.WISDOM_SIGNUP (set in layout/theme.liquid):
 *   companyId : Klaviyo public API key (site ID), safe to ship
 *   listId    : the "Wisdom Signups (opted in)" list
 *   images    : { popup, gate, foot } asset URLs
 *
 * Consent: email is required. Phone is optional, and only subscribed to SMS
 * when the visitor ticks the separate consent box. Phone is never required,
 * and consent is never a condition of playing or buying.
 */
(function () {
  'use strict';

  var CFG = window.WISDOM_SIGNUP || {};
  if (!CFG.companyId || !CFG.listId) return;
  var IMG = CFG.images || {};

  var DONE_KEY = 'wisdomSignupDone';        // captured on this browser
  var SNOOZE_KEY = 'wisdomSignupSnoozed';   // popup dismissed (ms timestamp)
  var SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
  var POPUP_DELAY_MS = 12000;
  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(CFG.companyId);

  /* Storage can throw (private mode, blocked site data); every use is guarded. */
  function read(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function write(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* fine */ } }

  function captured() { return read(DONE_KEY) === '1'; }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function isEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v); }

  /* US numbers only for now: 10 digits, or 11 starting with 1. Returns E.164 or ''. */
  function toE164(v) {
    var d = String(v || '').replace(/\D/g, '');
    if (d.length === 10) return '+1' + d;
    if (d.length === 11 && d.charAt(0) === '1') return '+' + d;
    return '';
  }

  function subscribe(email, phone, smsConsent, source) {
    var attrs = {
      email: email,
      properties: {
        'Signup Source': source,
        'Signup Page': window.location.pathname
      },
      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
    };
    if (phone && smsConsent) {
      attrs.phone_number = phone;
      attrs.subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
    }
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source,
          profile: { data: { type: 'profile', attributes: attrs } }
        },
        relationships: { list: { data: { type: 'list', id: CFG.listId } } }
      }
    };
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', 'revision': '2024-10-15' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('klaviyo ' + r.status);
      return true;
    });
  }

  /* ---------- Form markup (single source for all three places) ---------- */

  var uid = 0;
  function formHTML(o) {
    uid += 1;
    var id = 'w-su-' + uid;
    return '' +
      '<form class="w-su__form" novalidate data-source="' + esc(o.source) + '">' +
        '<label class="w-su__sr" for="' + id + '-e">Email</label>' +
        '<input class="w-su__input" id="' + id + '-e" name="email" type="email" inputmode="email" autocomplete="email" placeholder="Your email" required>' +
        '<div class="w-su__sms">' +
          '<div class="w-su__phone">' +
            '<label class="w-su__sr" for="' + id + '-p">Phone (optional)</label>' +
            '<input class="w-su__input w-su__input--phone" id="' + id + '-p" name="phone" type="tel" inputmode="tel" autocomplete="tel" placeholder="Phone for text drops (optional)">' +
            '<label class="w-su__check" hidden>' +
              '<input type="checkbox" name="sms">' +
              '<span>Text me Wisdom drops and offers. By ticking this box I agree to receive recurring automated marketing texts from Wisdom at the number above. Consent is not a condition of purchase. Msg &amp; data rates may apply. Reply STOP to cancel, HELP for help. ' +
              '<a href="/policies/privacy-policy">Privacy</a> &middot; <a href="/pages/sms-terms">Text terms</a></span>' +
            '</label>' +
          '</div>' +
        '</div>' +
        '<p class="w-su__error" role="alert" hidden></p>' +
        '<button class="w-su__btn" type="submit">' + esc(o.button) + '</button>' +
        '<p class="w-su__fine">By signing up you agree to get Wisdom emails. Unsubscribe anytime.</p>' +
      '</form>';
  }

  function wireForm(form, onSuccess) {
    var email = form.querySelector('[name=email]');
    var phone = form.querySelector('[name=phone]');
    var check = form.querySelector('.w-su__check');
    var sms = form.querySelector('[name=sms]');
    var err = form.querySelector('.w-su__error');
    var btn = form.querySelector('.w-su__btn');

    function fail(msg, field) {
      err.textContent = msg;
      err.hidden = false;
      if (field) field.focus();
    }

    /* The consent box only appears once there's a phone number to consent for. */
    phone.addEventListener('input', function () {
      var has = phone.value.replace(/\D/g, '').length > 0;
      check.hidden = !has;
      if (!has) sms.checked = false;
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      err.hidden = true;
      var em = email.value.trim();
      if (!isEmail(em)) return fail('Enter a valid email address.', email);

      var pn = '';
      if (phone.value.trim()) {
        pn = toE164(phone.value);
        if (!pn) return fail('Enter a 10-digit US phone number, or leave it blank.', phone);
        if (!sms.checked) return fail('Tick the text box to get texts, or clear the phone number.', sms);
      }

      btn.disabled = true;
      var label = btn.textContent;
      btn.textContent = 'One sec...';
      subscribe(em, pn, sms.checked, form.getAttribute('data-source')).then(function () {
        write(DONE_KEY, '1');
        onSuccess(form);
      }).catch(function () {
        btn.disabled = false;
        btn.textContent = label;
        fail('Something went wrong. Please try again in a moment.');
      });
    });
  }

  function media(url, cls, label) {
    if (!url) return '';
    return '<div class="w-su__media ' + cls + '" role="img" aria-label="' + esc(label) + '" style="background-image:url(\'' + esc(url) + '\')"></div>';
  }

  /* ---------- Footer band (inline) ---------- */

  function mountInline(el) {
    if (captured()) { el.hidden = true; return; }
    var source = el.getAttribute('data-source') || 'Footer';
    el.innerHTML =
      '<section class="w-su-band">' +
        '<div class="w-su-band__inner">' +
          // framed so the photo's zoom-in (wisdom-motion.js) stays inside its half
          '<div class="w-su-band__frame">' + media(IMG.foot, 'w-su-band__media', 'The Wisdom red room') + '</div>' +
          '<div class="w-su w-su--band">' +
            '<p class="w-su__eyebrow">Objects are closer than they appear.</p>' +
            '<h2 class="w-su__title">Come closer.</h2>' +
            '<p class="w-su__lead">New frames, new faces and drops land on the list first. Get the key to the red room.</p>' +
            formHTML({ source: source, button: 'Join the list' }) +
          '</div>' +
        '</div>' +
      '</section>';
    wireForm(el.querySelector('form'), function () {
      el.querySelector('.w-su--band').innerHTML =
        '<p class="w-su__eyebrow">You\'re on the list</p>' +
        '<h2 class="w-su__title">Welcome to the room.</h2>' +
        '<p class="w-su__lead">Watch your inbox. The first look is on its way.</p>';
    });
  }

  /* ---------- Modal (popup + game gate) ---------- */

  var modal = null;
  var lastFocus = null;

  function closeModal(done) {
    if (!modal) return;
    var m = modal;
    modal = null;
    document.removeEventListener('keydown', m._esc);
    m.parentNode.removeChild(m);
    document.documentElement.classList.remove('w-su-lock');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    if (m._onClose) m._onClose(!!done);
  }

  /* opts: { source, layout 'side'|'top', image, imageLabel, eyebrow, title, lead,
             button, skip, done:{eyebrow,title,lead}, onClose(done) } */
  function open(opts) {
    if (modal) return;
    lastFocus = document.activeElement;
    var m = document.createElement('div');
    m.className = 'w-su-modal';
    m.setAttribute('role', 'dialog');
    m.setAttribute('aria-modal', 'true');
    m.setAttribute('aria-label', String(opts.title).replace(/\|/g, ' '));
    var card =
      '<div class="w-su-modal__card w-su w-su-modal__card--' + (opts.layout || 'side') + '">' +
        media(opts.image, 'w-su-modal__media' + (opts.pixel ? ' w-su__media--pixel' : ''), opts.imageLabel || '') +
        '<div class="w-su-modal__body">' +
          '<button type="button" class="w-su-modal__close" aria-label="Close">&times;</button>' +
          '<p class="w-su__eyebrow">' + esc(opts.eyebrow) + '</p>' +
          '<h2 class="w-su__title">' + title(opts.title) + '</h2>' +
          '<p class="w-su__lead">' + esc(opts.lead) + '</p>' +
          formHTML({ source: opts.source, button: opts.button }) +
          (opts.skip ? '<button type="button" class="w-su__skip">' + esc(opts.skip) + '</button>' : '') +
        '</div>' +
      '</div>';
    m.innerHTML = card;
    m._onClose = opts.onClose;
    m._esc = function (e) { if (e.key === 'Escape') closeModal(false); };
    document.addEventListener('keydown', m._esc);
    m.addEventListener('click', function (e) { if (e.target === m) closeModal(false); });
    m.querySelector('.w-su-modal__close').addEventListener('click', function () { closeModal(false); });
    var skip = m.querySelector('.w-su__skip');
    if (skip) skip.addEventListener('click', function () { closeModal(false); });
    document.body.appendChild(m);
    document.documentElement.classList.add('w-su-lock');
    modal = m;
    wireForm(m.querySelector('form'), function () {
      var d = opts.done || {};
      m.querySelector('.w-su-modal__body').innerHTML =
        '<p class="w-su__eyebrow">' + esc(d.eyebrow || 'You\'re on the list') + '</p>' +
        '<h2 class="w-su__title">' + esc(d.title || 'Welcome to the room.') + '</h2>' +
        '<p class="w-su__lead">' + esc(d.lead || 'Watch your inbox. The first look is on its way.') + '</p>';
      window.setTimeout(function () { closeModal(true); }, 1800);
    });
    var first = m.querySelector('[name=email]');
    if (first) window.setTimeout(function () { first.focus(); }, 50);
  }

  /* ---------- Copy for the two modals ---------- */

  var POPUP = {
    source: 'Popup',
    layout: 'side',
    image: IMG.popup,
    imageLabel: 'A pair of Wisdom frames with amber lenses, seen through red blinds',
    eyebrow: 'Free early access',
    title: 'Step into|the red room.',
    lead: 'Drops sell out. The list gets new frames hours before anyone else, plus first look at every new face. Free to join.',
    button: 'Get early access',
    skip: 'No thanks, just looking'
  };

  var GATE = {
    source: 'Balloon Pop',
    layout: 'top',
    image: IMG.gate,
    pixel: true,
    imageLabel: 'Red pixel balloons floating above a row of spikes',
    eyebrow: 'Balloon Pop · 1 player',
    title: 'Insert email.|Press start.',
    lead: 'Save the balloons before they hit the spikes. Score high, win 15% off.',
    button: 'Let\u2019s play',
    done: { eyebrow: 'Player one ready', title: 'Don’t let them reach the spikes.', lead: 'Good luck.' }
  };

  /* Titles may carry "|" for a deliberate line break. */
  function title(t) { return esc(t).replace(/\|/g, '<br>'); }

  function merge(a, b) { var o = {}, k; for (k in a) o[k] = a[k]; for (k in b) o[k] = b[k]; return o; }

  /* ---------- First-visit popup ---------- */

  function maybePopup() {
    var p = window.location.pathname;
    if (captured()) return;
    if (/^\/(cart|checkouts|account|pages\/arcade)/.test(p)) return;   // the arcade has its own gate
    var snoozed = parseInt(read(SNOOZE_KEY), 10) || 0;
    if (snoozed && Date.now() - snoozed < SNOOZE_MS) return;
    window.setTimeout(function () {
      if (captured() || modal) return;
      open(merge(POPUP, { onClose: function (done) { if (!done) write(SNOOZE_KEY, String(Date.now())); } }));
    }, POPUP_DELAY_MS);
  }

  /* ---------- Public API (used by the Balloon Pop gate) ---------- */

  window.WisdomSignup = {
    captured: captured,
    /* Runs cb once the visitor is captured. Opens the game gate if they aren't yet. */
    gate: function (opts, cb) {
      if (captured()) { cb(); return; }
      open(merge(GATE, { onClose: function (done) { if (done) cb(); } }));
    }
  };

  function init() {
    var mounts = document.querySelectorAll('[data-w-signup-mount]');
    for (var i = 0; i < mounts.length; i++) mountInline(mounts[i]);
    maybePopup();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
